import { useState, useEffect } from "react";
import { useGetCreatorJobsQuery } from "@/redux/feature/jobs/jobsApi";
import JobsCard from "./components/JobsCard";
import {
  TJob,
  TJobsResponse,
  TOrderVideo,
} from "@/redux/feature/jobs/jobsType";
import { useAppSelector } from "@/redux/hooks/hooks";
import BlurPopUp from "./components/BlurPopUp";
import Modal from "@/components/ui/Modal";
import { X } from "lucide-react";
import { useCreatorStatus } from "@/hooks/useCreatorStatuts";
import usePolling from "@/hooks/usePolling";
import { JobsCardLoadingPlaceholder } from "./components/JobsCardLoadingPlaceholder";
import Pagination from "@/components/ui/Pagination";
import IncompleteApplicationReminderBanner from "../dashboard/components/IncompleteApplicationReminderBanner";
import ProjectConfirmModal from "./components/ProjectConfirmModal";
import ProjectConfirmationModal from "./components/ProjectConfirmationModal";

export type TFilterOptions = "available" | "applied" | "invited";
const FilterOptions = [
  {
    title: "Invited Jobs",
    value: "invited",
  },
  {
    title: "Available Jobs",
    value: "available",
  },
  {
    title: "Applied",
    value: "applied",
  },
];

const Jobs = () => {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("available");

  const [isProjectConfirmModalOpen, setIsProjectConfirmModalOpen] =
    useState(true); // Represents the videos currently shown in details view.
  const [visibleVideos, setVisibleVideos] = useState<TOrderVideo[]>([]); // Represents the videos currently shown in details view.
  const [isVideoDetailsModalOpen, setIsVideoDetailsModalOpen] = useState(false);
  // Get user email from Redux state
  const userEmail = useAppSelector((state) => state?.auth?.user?.email);

  const {
    shouldShowPopup,
    status,
    isLoading: isStatusLoading,
    isError: isStatusError,
  } = useCreatorStatus(userEmail as string);

  const renderPopup = () => {
    if (shouldShowPopup) {
      return <BlurPopUp status={status} />;
    }
    return null;
  };
  // Create polling intervals
  const jobRefreshPollingInterval = usePolling({
    templateIds: ["new_project_available", "creator_matching_orders_found"],
  }).pollingInterval;

  const shouldSkipJobs =
    !userEmail ||
    isStatusError ||
    shouldShowPopup ||
    (status?.isNotInitiated && !isStatusLoading);

  // Use the API query hook
  const {
    data: jobsResponse,
    isLoading,
    isFetching,
    error,
    isError,
  } = useGetCreatorJobsQuery(
    [
      {
        name: "email",
        value: userEmail,
      },
      {
        name: "sort",
        value: filter,
      },
      {
        name: "limit",
        value: 10,
      },
      { name: "page", value: page.toString() },
    ],
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: jobRefreshPollingInterval,
      skip: shouldSkipJobs, // Skip query if no email//!userEmail
    }
  );

  const jobsData = jobsResponse?.data?.data;
  const jobsDataMeta = jobsResponse?.data?.meta;
  console.log(jobsDataMeta);
  const getButtonClassName = (value: string) => {
    const baseClasses =
      "flex items-center rounded-[32px] border border-[#E4E7EC] px-4 py-2.5";
    return filter === value ? `${baseClasses} bg-[#F2F4F7]` : baseClasses;
  };

  if (error) {
    return <div>Error loading jobs</div>;
  }

  return (
    <div className="relative px-4 pt-4 md:px-11 md:mb-8 mb-28">
      {status?.isNotInitiated && <IncompleteApplicationReminderBanner />}
      {/* Popup container - positioned relative to the main content area */}
      {shouldShowPopup && (
        <div className="absolute w-full h-screen top-0 left-0  backdrop-blur-sm z-20 flex justify-center items-center">
          <div className=" rounded-xl shadow-lg max-w-lg w-full mx-auto">
            {renderPopup()}
          </div>
        </div>
      )}

      {/* Main content container */}

      {isError || isStatusError ? (
        <>
          <p className="py-20 flex justify-center items-center font-semibold animate-pulse">
            Some thing Went Wrong...
          </p>
        </>
      ) : (
        <div className={`${shouldShowPopup ? "filter blur-sm" : "pt-5"}`}>
          <div className="mb-6">
            <h3 className="text-lg leading-6 tracking-[-2.5%] font-semibold">
              {jobsDataMeta?.total || 0}{" "}
              {jobsDataMeta?.total === 1 ? "Job" : "Jobs"}
            </h3>
            <p className="text-[#98A2B3] text-xs leading-5 tracking-[-2%] font-medium">
              Available jobs for you
            </p>
          </div>
          <div>
            <div className=" overflow-x-auto lg:overflow-visible w-full md:flex justify-between items-center">
              <div className="flex gap-x-3 items-center">
                {FilterOptions.map((item) => (
                  <button
                    onClick={() => setFilter(item.value as TFilterOptions)}
                    key={item.value}
                    className={getButtonClassName(item.value)}
                  >
                    <h5 className="text-sm leading-5 whitespace-nowrap font-medium">
                      <span className="text-[#667085] ">{item.title}</span>{" "}
                    </h5>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            {isLoading || isFetching ? (
              <div className="mt-10 space-y-4">
                {Array.from({ length: 8 }, (_, index) => (
                  <JobsCardLoadingPlaceholder key={index} />
                ))}
              </div>
            ) : (
              <div className="mt-10 space-y-4">
                {jobsData?.map((job: TJob, i: number) => (
                  <JobsCard
                    filter={filter}
                    setVisibleVideos={setVisibleVideos}
                    setIsVideoDetailsModalOpen={setIsVideoDetailsModalOpen}
                    key={i}
                    job={job}
                  />
                ))}
              </div>
            )}
          </div>

          {jobsData?.length === 0 && (
            <div className="flex justify-center items-center h-full space-y-4">
              <p className="text-xl text-gray-400 font-semibold text-center my-14">
                No jobs are available for you.
              </p>
            </div>
          )}

          {jobsDataMeta && (
            <Pagination
              setPage={setPage}
              currentPage={page}
              totalPages={jobsDataMeta.totalPages}
            />
          )}
        </div>
      )}

      {/* show view details about the jobs modal */}

      <Modal
        setOpen={setIsVideoDetailsModalOpen}
        isOpen={isVideoDetailsModalOpen}
      >
        <ViewDetailsModal
          setIsVideoDetailsModalOpen={setIsVideoDetailsModalOpen}
          data={visibleVideos}
        />
      </Modal>

      {/* <Modal
        width="571px"
        padding="24px"
        setOpen={setIsProjectConfirmModalOpen}
        isOpen={isProjectConfirmModalOpen}
      >
        <ProjectConfirmationModal />
      </Modal> */}
    </div>
  );
};

export default Jobs;

const ViewDetailsModal = ({
  data,
  setIsVideoDetailsModalOpen,
}: {
  setIsVideoDetailsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: TOrderVideo[];
}) => {
  return (
    <div className="">
      <div className="flex  items-center justify-between">
        <p className="font-semibold text-lg">Order Video Details</p>

        <button
          className="text-gray-600"
          onClick={() => setIsVideoDetailsModalOpen(false)}
        >
          <X />
        </button>
      </div>

      <div className="pt-2">
        {data.map((item, i) => (
          <DetailsCard key={i} data={item} />
        ))}
      </div>
    </div>
  );
};

const DetailsCard = ({ data }: { data: TOrderVideo }) => {
  return (
    <div className="pb-4 pt-2 border-b border-gray-200 ">
      <h2 className="text-sm font-medium text-gray-600">
        {data.scriptDetails.titleOfTheVideo}
      </h2>

      <div className="flex flex-wrap gap-3 pt-2">
        <Badge value={data.scriptDetails?.videoLength?.label} />
        <Badge value={data.videoEditingDetails?.format} />
        <Badge value={data.scriptDetails?.videoTone.title} />
      </div>
    </div>
  );
};

const Badge = ({ value }: { value: string }) => (
  <p className="bg-[#F2F4F7] w-fit px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-2.5%] font-medium whitespace-nowrap">
    {value}
  </p>
);
